import { Box, Button, Grid, Modal, IconButton, Snackbar, Alert, Chip, Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import React from 'react';
import { useSelector } from 'react-redux';
import { LateSwapLineup, LateSwapPlayer } from './store/types';
import RoiBoostButton from '../common/Table/RoiBoostButton';
import { useLocalStorage } from 'usehooks-ts';
import { findLineupsToFavorite } from './utils';
import { SlateResponse } from '../common/store/types';
import { IRootState } from '../../store/reducer';

interface PlayerGroup {
  id: string;
  players: LateSwapPlayer[];
  roiBoost: number;
}

interface GroupRowProps {
  group: PlayerGroup;
  availablePlayers: LateSwapPlayer[];
  exposure: string;
  onPlayersChange: (groupId: string, players: LateSwapPlayer[]) => void;
  onBoostChange: (groupId: string, boost: number) => void;
  onDelete: (groupId: string) => void;
}

const GroupRow = React.memo(({ group, availablePlayers, exposure, onPlayersChange, onBoostChange, onDelete }: GroupRowProps) => {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 1,
        padding: 2,
        marginBottom: 2,
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={5}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            Players
          </Box>
          <Autocomplete
            multiple
            options={availablePlayers}
            value={group.players}
            getOptionLabel={(option) => {
              const posPrefix = ['CPT', 'FLEX'].includes(option.position?.toUpperCase())
                ? `${option.position} - `
                : '';
              return `${posPrefix}${option.name}`;
            }}
            isOptionEqualToValue={(option, value) => {
              // For Showdown, same player can be CPT or FLEX - treat them as separate
              const optionPos = option.position?.toUpperCase();
              const valuePos = value.position?.toUpperCase();
              if (['CPT', 'FLEX'].includes(optionPos) || ['CPT', 'FLEX'].includes(valuePos)) {
                return option.nameAndId === value.nameAndId && optionPos === valuePos;
              }
              return option.nameAndId === value.nameAndId;
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options.slice(0, 10);
              return options.filter(p =>
                p.name.toLowerCase().includes(inputValue.toLowerCase())
              ).slice(0, 10);
            }}
            onChange={(_, newValue) => onPlayersChange(group.id, newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={group.players.length === 0 ? "Search players..." : ""}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    flexWrap: 'wrap',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(219, 77, 0, 0.70)' },
                  },
                  '& .MuiInputBase-input': { color: 'white', fontSize: 13 },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((player, index) => {
                const pos = player.position?.toUpperCase();
                const uniqueKey = ['CPT', 'FLEX'].includes(pos)
                  ? `${player.nameAndId}||${pos}`
                  : player.nameAndId;
                return (
                <Chip
                  {...getTagProps({ index })}
                  key={uniqueKey}
                  label={['CPT', 'FLEX'].includes(player.position?.toUpperCase())
                    ? `${player.position} - ${player.name}`
                    : player.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: 13,
                    height: 28,
                    borderRadius: '14px',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: 18,
                      '&:hover': { color: 'white' },
                    },
                  }}
                />
              );
              })
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontSize: 13 }}>
                {['CPT', 'FLEX'].includes(option.position?.toUpperCase())
                  ? `${option.position} - ${option.name}`
                  : option.name}
              </Box>
            )}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={2}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            Group Exposure
          </Box>
          <Box sx={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
            {exposure}
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ fontSize: 11, fontWeight: 700, color: 'rgba(255, 255, 255, 0.7)', textTransform: 'uppercase', marginBottom: 0.5 }}>
            ROI Boost
          </Box>
          <RoiBoostButton
            defaultPercent={0}
            onChange={(percent) => onBoostChange(group.id, percent)}
            previousPercent={group.roiBoost}
          />
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => onDelete(group.id)}
            sx={{ color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#ff4444' } }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
});

interface OwnProps {
  readonly open: boolean;
  readonly players: LateSwapPlayer[];
  readonly closeModal: () => void;
  readonly selectedSlate: SlateResponse;
  readonly lineups: LateSwapLineup[];
  readonly contestName: string;
  readonly allowDupes: boolean;
  readonly playerExposureDict: { [key: string]: number };
  readonly stackExposureDict: { [key: string]: number };
  readonly playerRoiList: { [key: string]: number };
  readonly stackRoiList: { [key: string]: number };
  readonly playerGroups: PlayerGroup[];
  readonly setPlayerGroups: (groups: PlayerGroup[]) => void;
}

const LateSwapPlayerGroupsModal = (props: OwnProps) => {
  const {
    open,
    players,
    closeModal,
    selectedSlate,
    lineups,
    contestName,
    allowDupes,
    playerExposureDict,
    stackExposureDict,
    playerRoiList,
    stackRoiList,
    playerGroups,
    setPlayerGroups,
  } = props;

  // Get unique limit from Redux state (set by QuickFavorite)
  const { uniqueLimit } = useSelector((state: IRootState) => state.common);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  // Local state for editing - only committed when "Adjust Favorites" is clicked
  const [localGroups, setLocalGroupsBase] = React.useState<PlayerGroup[]>(playerGroups);

  // Keep ref to the latest localGroups for use in callbacks to avoid stale closures
  const localGroupsRef = React.useRef(localGroups);

  // Update ref synchronously during render
  localGroupsRef.current = localGroups;

  // Wrapper for setLocalGroups that also updates the ref immediately
  const setLocalGroups = React.useCallback((updater: PlayerGroup[] | ((prev: PlayerGroup[]) => PlayerGroup[])) => {
    setLocalGroupsBase(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;
      localGroupsRef.current = newValue; // Update ref immediately
      return newValue;
    });
  }, []);

  // Sync local state only when modal opens (not on every playerGroups change to avoid re-render cascade)
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened - sync with parent state
      setLocalGroups(playerGroups);
    }
    prevOpenRef.current = open;
  }, [open, playerGroups, setLocalGroups]);

  const [savedLineups, setSavedLineups] = useLocalStorage<LateSwapLineup[]>(
    "lateswap-lineups",
    []
  );

  // Filter out locked players
  const availablePlayers = React.useMemo(() => {
    return players.filter(p => !p.locked);
  }, [players]);

  // Sort players by exposure (highest first) for better default ordering in autocomplete
  const sortedPlayers = React.useMemo(() => {
    return [...availablePlayers].sort((a, b) => (b.postSwapExposure || 0) - (a.postSwapExposure || 0));
  }, [availablePlayers]);

  const createNewGroup = () => {
    const newGroup: PlayerGroup = {
      id: `group-${Date.now()}`,
      players: [],
      roiBoost: 0,
    };
    setLocalGroups(prev => [...prev, newGroup]);
  };

  const deleteGroup = React.useCallback((groupId: string) => {
    setLocalGroups(prev => prev.filter(g => g.id !== groupId));
  }, []);

  const updateGroupPlayers = React.useCallback((groupId: string, newPlayers: LateSwapPlayer[]) => {
    setLocalGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return { ...g, players: newPlayers };
      }
      return g;
    }));
  }, []);

  const updateGroupBoost = React.useCallback((groupId: string, boost: number) => {
    setLocalGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return { ...group, roiBoost: boost };
      }
      return group;
    }));
  }, []);

  // Calculate group exposure - percentage of saved lineups that contain ALL players in the group
  // For Showdown, CPT is always the first player in exportableLineup, rest are FLEX
  const getGroupExposure = (group: PlayerGroup): string => {
    if (group.players.length === 0 || savedLineups.length === 0) return '0%';

    const lineupsWithAllPlayers = savedLineups.filter(lineup => {
      const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
      const cptPlayerId = lineupPlayerIds[0]; // First player is CPT
      const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX

      return group.players.every(player => {
        const playerPos = player.position?.toUpperCase();
        if (playerPos === 'CPT') {
          return player.nameAndId === cptPlayerId;
        } else if (playerPos === 'FLEX') {
          return flexPlayerIds.includes(player.nameAndId);
        }
        // Non-showdown: just check if player is in lineup
        return lineupPlayerIds.includes(player.nameAndId);
      });
    });

    const exposure = (lineupsWithAllPlayers.length / savedLineups.length) * 100;
    return `${exposure.toFixed(1)}%`;
  };

  // Get players available for a specific group (not already in that group)
  // For Showdown, CPT and FLEX are treated as separate players
  // Uses sortedPlayers (sorted by exposure) for better default ordering
  const getAvailablePlayersForGroup = (group: PlayerGroup) => {
    const groupPlayerKeys = group.players.map(p => {
      const pos = p.position?.toUpperCase();
      return ['CPT', 'FLEX'].includes(pos)
        ? `${p.nameAndId}||${pos}`
        : p.nameAndId;
    });
    return sortedPlayers.filter(p => {
      const pos = p.position?.toUpperCase();
      const playerKey = ['CPT', 'FLEX'].includes(pos)
        ? `${p.nameAndId}||${pos}`
        : p.nameAndId;
      return !groupPlayerKeys.includes(playerKey);
    });
  };

  // Calculate boosted ROI for a lineup considering group boosts
  // For Showdown, CPT is always the first player in exportableLineup, rest are FLEX
  const getLineupGroupBoost = (lineup: LateSwapLineup, groups: PlayerGroup[]): number => {
    let totalGroupBoost = 0;
    const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
    const cptPlayerId = lineupPlayerIds[0]; // First player is CPT
    const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX

    groups.forEach(group => {
      if (group.players.length > 0 && group.roiBoost !== 0) {
        // Check if ALL players in the group are in this lineup (respecting CPT/FLEX position)
        const allPlayersInLineup = group.players.every(player => {
          const playerPos = player.position?.toUpperCase();
          if (playerPos === 'CPT') {
            return player.nameAndId === cptPlayerId;
          } else if (playerPos === 'FLEX') {
            return flexPlayerIds.includes(player.nameAndId);
          }
          // Non-showdown: just check if player is in lineup
          return lineupPlayerIds.includes(player.nameAndId);
        });
        if (allPlayersInLineup) {
          totalGroupBoost += group.roiBoost;
        }
      }
    });

    return totalGroupBoost / 100; // Convert to decimal like other boosts
  };

  const handleAdjustFavorites = () => {
    // Show snackbar immediately before any state updates
    setSnackbarOpen(true);

    // Use ref to get the latest value (avoids stale closure issues)
    const currentGroups = localGroupsRef.current;

    // Commit local changes to parent state
    setPlayerGroups(currentGroups);

    // Refavorite lineups when we have saved lineups
    // Don't clear savedLineups first to avoid visual flash
    if (savedLineups.length > 0) {
      setTimeout(() => {
        // We need to modify the lineups to include group boosts before favoriting
        // The group boost is added to simLineupRoi because findLineupsToFavorite calculates:
        //   finalRoi = simLineupRoi + roiBoost (from boostedPlayers)
        // and roiBoost gets overwritten by getBoostRoiForLineup, so we can't use it for groups
        const lineupsWithGroupBoosts = lineups.map(lineup => {
          if (lineup.lateSwapOptions) {
            return {
              ...lineup,
              lateSwapOptions: lineup.lateSwapOptions.map(option => {
                const groupBoost = getLineupGroupBoost(option, currentGroups);
                return {
                  ...option,
                  // Add group boost to simLineupRoi so it's included in the final ROI calculation
                  simLineupRoi: (option.simLineupRoi || 0) + groupBoost,
                };
              }),
            };
          }
          return lineup;
        });

        const result = findLineupsToFavorite(
          lineupsWithGroupBoosts,
          players,
          [],
          playerExposureDict,
          stackExposureDict,
          playerRoiList,
          [],
          contestName,
          selectedSlate,
          allowDupes,
          stackRoiList,
          uniqueLimit || undefined
        );
        setSavedLineups(result.lineups as LateSwapLineup[]);
      }, 100);
    }
  };

  const handleClearBoosts = () => {
    setLocalGroups(prev => prev.map(group => ({ ...group, roiBoost: 0 })));
  };

  const handleDeleteAllGroups = () => {
    setLocalGroups(() => []);
    // Also commit to parent state immediately so deletion persists even if modal is closed
    setPlayerGroups([]);
  };

  // Save groups when modal closes (so groups persist even without clicking Adjust Favorites)
  const handleClose = () => {
    setPlayerGroups(localGroupsRef.current);
    closeModal();
  };

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        keepMounted
        onClose={(_event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') handleClose();
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(125, 125, 125, .65)',
              transition: 'none',
            },
          },
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontFamily: 'Roboto',
        }}
      >
        <Box sx={{
          backgroundColor: '#0A0A0A',
          borderRadius: 2,
          minHeight: 400,
          width: 900,
          maxHeight: '80vh',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, .30)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 0 20px' }}>
            <Box>
              <Box sx={{ fontSize: 20, fontWeight: 600, marginBottom: 1 }}>Player Groups</Box>
              <Box sx={{ fontSize: 14, fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 600, lineHeight: 1.4 }}>
                Create player groups to boost lineups that include all players in the group. The boost only applies when every player in the group appears in the lineup.
              </Box>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Create New Group Button */}
          <Box sx={{ padding: '16px 20px 0 20px' }}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={createNewGroup}
              sx={{
                color: 'white',
                borderColor: 'rgba(219, 77, 0, 0.70)',
                '&:hover': {
                  borderColor: 'rgba(219, 77, 0, 0.90)',
                  backgroundColor: 'rgba(219, 77, 0, 0.1)',
                },
              }}
            >
              CREATE NEW GROUP
            </Button>
          </Box>

          {/* Groups List */}
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'slategrey', borderRadius: '4px' },
          }}>
            {localGroups.length === 0 ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 150,
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: 14,
              }}>
                No groups created yet. Click "Create New Group" to get started.
              </Box>
            ) : (
              localGroups.map((group) => (
                <GroupRow
                  key={group.id}
                  group={group}
                  availablePlayers={getAvailablePlayersForGroup(group)}
                  exposure={getGroupExposure(group)}
                  onPlayersChange={updateGroupPlayers}
                  onBoostChange={updateGroupBoost}
                  onDelete={deleteGroup}
                />
              ))
            )}
          </Box>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundColor: '#0A0A0A'
          }}>
            <Button
              variant="outlined"
              onClick={handleDeleteAllGroups}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              DELETE ALL GROUPS
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleClearBoosts}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                CLEAR BOOSTS
              </Button>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                onClick={handleAdjustFavorites}
                sx={{
                  backgroundColor: 'rgba(219, 77, 0, 0.70)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(219, 77, 0, 0.90)' }
                }}
              >
                ADJUST FAVORITES
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Favorites updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default LateSwapPlayerGroupsModal;